import React, { useEffect, useMemo, useState, Fragment } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { CalendarClock, PhoneCall, CheckCircle2, XCircle, CalendarPlus, AlertTriangle } from 'lucide-react';
/** Returns ms until the start of the next minute. Used to schedule a tick that
 *  keeps the "now" reference fresh so urgency colors stay correct in real time. */
const msUntilNextMinute = () => {
  const now = new Date();
  return 60000 - (now.getSeconds() * 1000 + now.getMilliseconds());
};
type Urgency = 'overdue' | 'today' | 'soon';
interface ReviewRow {
  encounterId: string;
  patientId: string;
  patientName: string;
  bmcFileNumber: string;
  phone: string;
  reviewDate: Date;
  reviewDateISO: string;
  status: 'Pending' | 'Contacted' | 'Cancelled' | 'Rescheduled';
  contactedAt?: string;
  urgency: Urgency;
}
const urgencyStyles: Record<Urgency, {
  row: string;
  pill: string;
  label: string;
}> = {
  overdue: {
    row: 'bg-red-50 border-l-4 border-red-500 hover:bg-red-100',
    pill: 'bg-red-600 text-white',
    label: 'Overdue'
  },
  today: {
    row: 'bg-orange-50 border-l-4 border-orange-500 hover:bg-orange-100',
    pill: 'bg-orange-500 text-white',
    label: 'Due today'
  },
  soon: {
    row: 'bg-green-50 border-l-4 border-green-500 hover:bg-green-100',
    pill: 'bg-green-600 text-white',
    label: 'Due within 24 h'
  }
};
const isSameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const classifyUrgency = (reviewDate: Date, now: Date): Urgency | null => {
  // Window: from now back to overdue (any past) through now + 24h.
  // - overdue: review date is strictly in the past
  // - today: same calendar day as `now` AND not overdue
  // - soon: review date is in the future, within the next 24 hours, and NOT today
  if (reviewDate.getTime() < now.getTime()) return 'overdue';
  if (isSameDay(reviewDate, now)) return 'today';
  const diffMs = reviewDate.getTime() - now.getTime();
  if (diffMs <= 24 * 60 * 60 * 1000) return 'soon';
  return null; // too far in the future to display
};
export const PatientReviewDueList: React.FC = () => {
  const {
    encounters,
    patients,
    updateEncounter,
    currentUser
  } = useData();
  const [now, setNow] = useState<Date>(() => new Date());
  // For the inline reschedule mini-form. Maps encounterId → new YYYY-MM-DDTHH:mm
  const [rescheduling, setRescheduling] = useState<Record<string, string>>({});
  // Re-tick at the top of each minute so urgency classifications stay fresh.
  useEffect(() => {
    let timeoutId: number;
    const schedule = () => {
      timeoutId = window.setTimeout(() => {
        setNow(new Date());
        schedule();
      }, msUntilNextMinute());
    };
    schedule();
    return () => window.clearTimeout(timeoutId);
  }, []);
  const rows: ReviewRow[] = useMemo(() => {
    const out: ReviewRow[] = [];
    for (const enc of encounters) {
      if (!enc.reviewDate) continue;
      if (enc.reviewStatus === 'Cancelled') continue;
      const reviewDate = new Date(enc.reviewDate);
      if (Number.isNaN(reviewDate.getTime())) continue;
      const urgency = classifyUrgency(reviewDate, now);
      if (!urgency) continue;
      const patient = patients.find(p => p.id === enc.patientId);
      if (!patient) continue;
      out.push({
        encounterId: enc.id,
        patientId: patient.id,
        patientName: `${patient.firstName} ${patient.lastName}`,
        bmcFileNumber: patient.bmcFileNumber,
        phone: patient.contact || '—',
        reviewDate,
        reviewDateISO: enc.reviewDate,
        status: enc.reviewStatus || 'Pending',
        contactedAt: enc.reviewContactedAt,
        urgency
      });
    }
    // Most urgent first: overdue → today → soon. Within a bucket, earliest review date first.
    const urgencyRank: Record<Urgency, number> = {
      overdue: 0,
      today: 1,
      soon: 2
    };
    return out.sort((a, b) => {
      const u = urgencyRank[a.urgency] - urgencyRank[b.urgency];
      if (u !== 0) return u;
      return a.reviewDate.getTime() - b.reviewDate.getTime();
    });
  }, [encounters, patients, now]);
  const counts = useMemo(() => ({
    overdue: rows.filter(r => r.urgency === 'overdue').length,
    today: rows.filter(r => r.urgency === 'today').length,
    soon: rows.filter(r => r.urgency === 'soon').length
  }), [rows]);
  const handleMarkContacted = (encounterId: string) => {
    updateEncounter(encounterId, {
      reviewStatus: 'Contacted',
      reviewContactedAt: new Date().toISOString(),
      reviewContactedBy: currentUser?.id
    });
  };
  const handleCancelReview = (encounterId: string, patientName: string) => {
    if (!window.confirm(`Cancel the scheduled review for ${patientName}? This removes them from the due list.`)) return;
    updateEncounter(encounterId, {
      reviewStatus: 'Cancelled'
    });
  };
  const handleOpenReschedule = (row: ReviewRow) => {
    // Pre-fill with the existing review date so the receptionist only edits if needed.
    const d = row.reviewDate;
    const pad = (n: number) => String(n).padStart(2, '0');
    const local = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    setRescheduling(r => ({
      ...r,
      [row.encounterId]: local
    }));
  };
  const handleConfirmReschedule = (encounterId: string) => {
    const next = rescheduling[encounterId];
    if (!next) return;
    const asDate = new Date(next);
    if (Number.isNaN(asDate.getTime())) {
      alert('Please pick a valid date and time.');
      return;
    }
    updateEncounter(encounterId, {
      reviewDate: asDate.toISOString(),
      reviewStatus: 'Rescheduled',
      reviewContactedAt: new Date().toISOString(),
      reviewContactedBy: currentUser?.id
    });
    setRescheduling(r => {
      const {
        [encounterId]: _,
        ...rest
      } = r;
      return rest;
    });
  };
  const handleCancelReschedule = (encounterId: string) => {
    setRescheduling(r => {
      const {
        [encounterId]: _,
        ...rest
      } = r;
      return rest;
    });
  };
  return <Card title="Patients Due for Review Within 24 Hours" subtitle={rows.length === 0 ? 'No patients due for review in the next 24 hours.' : `${rows.length} patient${rows.length === 1 ? '' : 's'} need follow-up.`} data-id="element-4916">
      {/* Summary chips */}
      <div className="flex flex-wrap gap-2 mb-4" data-id="element-4917">
        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-100 text-red-800 text-xs font-semibold" data-id="element-4918">
          <AlertTriangle className="w-3.5 h-3.5" data-id="element-4919" /> Overdue: {counts.overdue}
        </span>
        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-100 text-orange-800 text-xs font-semibold" data-id="element-4920">
          <CalendarClock className="w-3.5 h-3.5" data-id="element-4921" /> Due today: {counts.today}
        </span>
        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-100 text-green-800 text-xs font-semibold" data-id="element-4922">
          <CalendarClock className="w-3.5 h-3.5" data-id="element-4923" /> Due within 24 h:{' '}
          {counts.soon}
        </span>
        <span className="ml-auto text-xs text-gray-500 italic" data-id="element-4924">
          Live · refreshed {now.toLocaleTimeString()}
        </span>
      </div>

      {rows.length === 0 ? <div className="text-center text-gray-500 py-10" data-id="element-4925">
          <CalendarClock className="w-10 h-10 mx-auto mb-2 text-gray-300" data-id="element-4926" />
          <p className="text-sm" data-id="element-4927">All caught up — no upcoming reviews.</p>
        </div> : <div className="overflow-x-auto" data-id="element-4928">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-4929">
            <thead className="bg-gray-50" data-id="element-4930">
              <tr data-id="element-4931">
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-600" data-id="element-4932">
                  Status
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-600" data-id="element-4933">
                  Full Name
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-600" data-id="element-4934">
                  Phone Number
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-600" data-id="element-4935">
                  Review Date
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-600" data-id="element-4936">
                  File No.
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-600" data-id="element-4937">
                  Follow-up
                </th>
                <th className="px-4 py-2 text-right text-xs font-semibold uppercase text-gray-600" data-id="element-4938">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100" data-id="element-4939">
              {rows.map(row => {
            const style = urgencyStyles[row.urgency];
            const isRescheduling = rescheduling[row.encounterId] !== undefined;
            return <Fragment key={row.encounterId} data-id="element-4940">
                    <tr className={`transition-colors ${style.row}`} data-id="element-4941">
                      <td className="px-4 py-2 align-top" data-id="element-4942">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-bold ${style.pill}`} data-id="element-4943">
                          {style.label}
                        </span>
                      </td>
                      <td className="px-4 py-2 align-top font-medium text-gray-900" data-id="element-4944">
                        {row.patientName}
                      </td>
                      <td className="px-4 py-2 align-top text-gray-700" data-id="element-4945">
                        {row.phone}
                      </td>
                      <td className="px-4 py-2 align-top text-gray-700 whitespace-nowrap" data-id="element-4946">
                        {row.reviewDate.toLocaleString([], {
                    dateStyle: 'medium',
                    timeStyle: 'short'
                  })}
                      </td>
                      <td className="px-4 py-2 align-top font-mono text-sm text-blue-700" data-id="element-4947">
                        {row.bmcFileNumber}
                      </td>
                      <td className="px-4 py-2 align-top" data-id="element-4948">
                        {row.status === 'Contacted' ? <Badge variant="success" data-id="element-4949">
                            <CheckCircle2 className="w-3 h-3 mr-1 inline" data-id="element-4950" />
                            Contacted
                          </Badge> : row.status === 'Rescheduled' ? <Badge variant="info" data-id="element-4951">Rescheduled</Badge> : <Badge variant="warning" data-id="element-4952">Pending</Badge>}
                        {row.contactedAt && row.status === 'Contacted' && <div className="text-[10px] text-gray-500 mt-0.5 italic" data-id="element-4953">
                            {new Date(row.contactedAt).toLocaleString()}
                          </div>}
                      </td>
                      <td className="px-4 py-2 align-top text-right" data-id="element-4954">
                        <div className="inline-flex flex-wrap gap-1 justify-end" data-id="element-4955">
                          {row.status !== 'Contacted' && <Button size="sm" variant="primary" onClick={() => handleMarkContacted(row.encounterId)} title="Mark as contacted" data-id="element-4956">
                              <PhoneCall className="w-3.5 h-3.5 mr-1" data-id="element-4957" />
                              Mark Contacted
                            </Button>}
                          <Button size="sm" variant="outline" onClick={() => handleOpenReschedule(row)} title="Reschedule review" data-id="element-4958">
                            <CalendarPlus className="w-3.5 h-3.5 mr-1" data-id="element-4959" />
                            Reschedule
                          </Button>
                          <Button size="sm" variant="danger" onClick={() => handleCancelReview(row.encounterId, row.patientName)} title="Cancel review" data-id="element-4960">
                            <XCircle className="w-3.5 h-3.5 mr-1" data-id="element-4961" />
                            Cancel
                          </Button>
                        </div>
                      </td>
                    </tr>
                    {isRescheduling && <tr className="bg-blue-50 border-b border-blue-200" data-id="element-4962">
                        <td colSpan={7} className="px-4 py-3" data-id="element-4963">
                          <div className="flex flex-wrap items-end gap-3" data-id="element-4964">
                            <div data-id="element-4965">
                              <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-4966">
                                New review date & time
                              </label>
                              <input type="datetime-local" value={rescheduling[row.encounterId]} onChange={e => setRescheduling(r => ({
                        ...r,
                        [row.encounterId]: e.target.value
                      }))} className="px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none" data-id="element-4967" />
                            </div>
                            <Button size="sm" variant="primary" onClick={() => handleConfirmReschedule(row.encounterId)} data-id="element-4968">
                              Confirm new date
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleCancelReschedule(row.encounterId)} data-id="element-4969">
                              Cancel
                            </Button>
                          </div>
                        </td>
                      </tr>}
                  </Fragment>;
          })}
            </tbody>
          </table>
        </div>}
    </Card>;
};